// Rendering the cost screens' figures.
//
// The rollup's `currency` is a plain string on the wire — a meter carries
// whatever currency it was written in — while `formatMoney` takes the
// contract's `Currency`, which is the two this business bills in. So the string
// is PARSED, never asserted: a meter in a third currency renders as a plain
// amount with its code beside it rather than as a USD figure that is wrong by
// the exchange rate.

import { Currency } from "@crm/shared";
import type { UsageCostResponse } from "@crm/shared";
import { formatMoney } from "../../format/money.js";

/** The contract's currency, or null when the wire carried one this app cannot format. */
export function asCurrency(raw: string): Currency | null {
  const parsed = Currency.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * An amount in whatever currency the rollup reported.
 *
 * `omitCurrencyCode` is never used here: this app shows CAD and USD, both spelt
 * `$`, and a cost screen that names the currency once in a header is a screen
 * where somebody reads a USD figure as a CAD one.
 */
export function formatUsageMoney(minor: number, currency: string): string {
  const known = asCurrency(currency);
  if (known !== null) return formatMoney(minor, known);
  return `${(minor / 100).toFixed(2)} ${currency}`;
}

/**
 * Facts the month could not price, over every group.
 *
 * The three reasons summed, never `facts_scanned - facts_priced`: two meters may
 * both match one fact and their costs add, so those two counts are not on the
 * same scale.
 */
export function unpricedCount(report: UsageCostResponse): number {
  return report.groups.reduce(
    (sum, group) =>
      sum +
      group.unpriced_by_reason.null_field +
      group.unpriced_by_reason.rate_unavailable +
      group.unpriced_by_reason.division_by_zero,
    0,
  );
}

/** `14:02` — when the figures were computed, which is all the header has room for. */
export function formatClock(instant: string): string {
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return instant;
  return at.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/**
 * The first eight characters of a run id: enough to compare two runs by eye.
 *
 * Re-exported rather than defined here since the billing screens started naming
 * runs too — one rule, so a run cut to eight characters on the cost screens
 * cannot be cut to six on the close.
 */
export { shortRunId } from "../../format/labels.js";
